import { produce } from 'immer';
import { Decimal } from 'decimal.js';
import { groupBy } from 'ramda';
import { assetUtils, numberUtils } from '@zen/common-utils';
import * as OrderConverter from '../../../utils/OrderConverter';
import FilterTypes from './filterTypes';

const { getAssetFullId } = assetUtils;
const { toValue } = numberUtils;

export const ACTIONS = {
  RESET: 'RESET',
  FILTER_CHANGED: 'FILTER_CHANGED',
  FETCH_EXECUTIONS: {
    REQUESTED: 'FETCH_EXECUTIONS_REQUESTED',
    SUCCEEDED: 'FETCH_EXECUTIONS_SUCCEEDED',
    FAILED: 'FETCH_EXECUTIONS_FAILED',
  },
  FETCH_TRANSACTIONS: {
    REQUESTED: 'FETCH_TRANSACTIONS_REQUESTED',
    SUCCEEDED: 'FETCH_TRANSACTIONS_SUCCEEDED',
    FAILED: 'FETCH_TRANSACTIONS_FAILED',
  },
};

/**
 * The options for contract operations and the string which will appear in the data property
 */
export const OPERATIONS = {
  OPEN_ORDER: 'open',
  CANCEL_ORDER: 'cancel',
  TAKE_ORDER: 'take',
  ORDER_TAKEN: 'taken',
  PARTIAL_RESULT: 'partial_result',
};

const reducer = produce((draft, action) => {
  switch (action.type) {
    case ACTIONS.RESET:
      draft.pkHash = action.payload.pkHash;
      draft.transactions = [];
      draft.operations = [];
      draft.executions = [];
      draft.items = [];
      draft.nonPendingTxCount = 0;
      draft.filter = '';
      draft.loading = {
        executions: false,
        transactions: false,
        items: false,
      };
      break;
    case ACTIONS.FILTER_CHANGED:
      draft.filter = action.payload;
      filterOperations(draft);
      break;
    case ACTIONS.FETCH_TRANSACTIONS.REQUESTED:
      draft.loading.transactions = true;
      draft.loading.items = true;
      break;
    case ACTIONS.FETCH_TRANSACTIONS.SUCCEEDED:
      _handleTransactionsChanged(draft, action.payload);
      // process only the changed txs, then concat to the rest
      draft.operations = computeOperations(draft, action.payload).concat(
        draft.operations.slice(indexOrLength(draft.operations, (o) => !o.data.pending))
      );
      filterOperations(draft);
      draft.loading.transactions = false;
      draft.loading.items = false;
      break;
    case ACTIONS.FETCH_TRANSACTIONS.FAILED:
      draft.loading.transactions = false;
      draft.loading.items = false;
      break;
    case ACTIONS.FETCH_EXECUTIONS.REQUESTED:
      draft.loading.executions = true;
      break;
    case ACTIONS.FETCH_EXECUTIONS.SUCCEEDED:
      _handleExecutionsChanged(draft, action.payload);
      draft.operations = computeOperations(draft);
      filterOperations(draft);
      draft.loading.executions = false;
      break;
    case ACTIONS.FETCH_EXECUTIONS.FAILED:
      draft.loading.executions = false;
      break;
    default:
      throw new Error(`Unhandled action type ${action.type}`);
  }
});

export default reducer;

function _handleTransactionsChanged(draft, items = []) {
  const txsWithConfirmations = draft.transactions.slice(
    indexOfFirstTxConfirmationOrLength(draft.transactions)
  );
  draft.transactions = items.concat(txsWithConfirmations);
  draft.nonPendingTxCount =
    draft.transactions.length - indexOfFirstTxConfirmationOrLength(draft.transactions);
}

function _handleExecutionsChanged(draft, items) {
  if (items.length) {
    draft.executions = items;
  }
}

function filterOperations(draft) {
  switch (draft.filter) {
    case FilterTypes.CANCELED:
      draft.items = computeCanceledOrders(draft);
      break;
    case FilterTypes.FILLED:
      draft.items = computeFilledOrders(draft);
      break;
    case FilterTypes.OPEN:
      draft.items = computeOpenOrders(draft);
      break;
    case FilterTypes.PARTIAL_FILLED:
      draft.items = computePartialFilledOrders(draft);
      break;
    case FilterTypes.PENDING:
      draft.items = computePendingOrders(draft);
      break;
    case FilterTypes.TRANSACTIONS:
      draft.items = computeSendReceivedTransactions(draft);
      break;
    case FilterTypes.TX_RECEIVE:
      draft.items = computeReceivedTransactions(draft);
      break;
    case FilterTypes.TX_SEND:
      draft.items = computeSendTransactions(draft);
      break;

    default:
      draft.items = draft.operations;
      break;
  }
}

const groupByTxHash = groupBy((item) => item.txHash);

/**
 * Combines between transactions and DEX operations to create a combined list of operations
 */
function computeOperations(draft, transactions) {
  const pkHash = draft.pkHash;
  const txsToProcess = transactions ? transactions : draft.transactions;
  const transactionsGrouped = groupByTxHash(txsToProcess);
  const executionsGrouped = groupByTxHash(draft.executions);
  const handledTxHashes = [];
  const operations = [];

  // go by draft.transactions to keep the sorting order by confirmations
  txsToProcess.forEach(({ txHash }) => {
    if (handledTxHashes.includes(txHash)) return;
    handledTxHashes.push(txHash); // make sure same txHash is not handled twice

    // specific txHash transaction items
    const transactionItems = transactionsGrouped[txHash];
    const executions = executionsGrouped[txHash] || [];

    // adding first the DEX actions, then the rest/leftovers of the transactions
    executions.forEach((execution) => {
      if (isMake(execution)) {
        // user sends token in the transaction
        const index = transactionItems.findIndex((tx) => isMatchingMake({ execution, tx }));
        const transactionItem = transactionItems[index];

        if (transactionItem) {
          operations.push(getExecutionOperation({ execution, pkHash, tx: transactionItem }));

          // reduce amount in the tx item for later, replace immutable object
          transactionItems[index] = Object.assign({}, transactionItem, {
            amount: toValue(new Decimal(transactionItem.amount).plus(execution.underlyingAmount)),
          });
        }
      } else if (isPartialTakeResult(execution)) {
        const transactionItem = transactionItems.find((tx) =>
          isMatchingPartialTakeResult({ execution, tx })
        );

        if (transactionItem) {
          operations.push(getExecutionOperation({ execution, pkHash, tx: transactionItem }));
          // no need to reduce amount: this is a result of a partial take, amount was handled by the take
        }
      } else if (isCancel(execution)) {
        // user received token in the transaction
        const index = transactionItems.findIndex((tx) => isMatchingCancel({ execution, tx }));
        const transactionItem = transactionItems[index];

        if (transactionItem) {
          operations.push(getExecutionOperation({ execution, pkHash, tx: transactionItem }));

          // reduce amount in the tx item for later, replace immutable object
          transactionItems[index] = Object.assign({}, transactionItem, {
            amount: toValue(new Decimal(transactionItem.amount).minus(execution.underlyingAmount)),
          });
        }
      } else if (pkHash === execution.takerPkHash) {
        // TAKER: user sends and receives token in the transaction
        const sentIndex = transactionItems.findIndex(
          (tx) => getAssetFullId(execution.pairAsset) === getAssetFullId(tx.asset)
        );
        const receivedIndex = transactionItems.findIndex(
          (tx) => getAssetFullId(execution.underlyingAsset) === getAssetFullId(tx.asset)
        );

        if (sentIndex >= 0 && receivedIndex >= 0) {
          const sent = transactionItems[sentIndex];
          const received = transactionItems[receivedIndex];
          operations.push(getExecutionOperation({ execution, pkHash, tx: sent }));

          // reduce amount in the tx item for later, replace immutable object
          transactionItems[sentIndex] = Object.assign({}, sent, {
            amount: toValue(new Decimal(sent.amount).plus(execution.providedAmount)),
          });
          transactionItems[receivedIndex] = Object.assign({}, received, {
            amount: toValue(new Decimal(received.amount).minus(execution.requestedPayout)),
          });
        }
      } else {
        // GIVER: user receives token in the transaction
        const index = transactionItems.findIndex((tx) => isMatchingGiver({ execution, tx }));
        const transactionItem = transactionItems[index];

        if (transactionItem) {
          operations.push(getExecutionOperation({ execution, pkHash, tx: transactionItem }));

          // reduce amount in the tx item for later, replace immutable object
          transactionItems[index] = Object.assign({}, transactionItem, {
            amount: toValue(new Decimal(transactionItem.amount).minus(execution.providedAmount)),
          });
        }
      }
    });

    // go over the transaction items and add the left overs
    transactionItems.forEach((transactionItem) => {
      if (!new Decimal(transactionItem.amount).isZero()) {
        operations.push(getTransactionOperation({ tx: transactionItem }));
      }
    });
  });

  return operations;
}

function computeOpenOrders(draft) {
  return draft.operations.filter(
    (operation) => operation.data.status === 'open' && !operation.data.pending
  );
}
function computeCanceledOrders(draft) {
  return draft.operations.filter(
    (operation) =>
      operation.data.status === 'canceled' &&
      operation.data.command === 'cancel' &&
      !operation.data.pending
  );
}
function computeFilledOrders(draft) {
  return draft.operations.filter(
    (operation) =>
      operation.data.status === 'filled' &&
      operation.data.command === 'take' &&
      !operation.data.pending
  );
}
function computePartialFilledOrders(draft) {
  return draft.operations.filter(
    (operation) =>
      operation.data.status === 'partial_fill' &&
      operation.data.command === 'take' &&
      !operation.data.pending
  );
}
function computePendingOrders(draft) {
  return draft.operations.filter((operation) => operation.data.pending);
}
function computeSendReceivedTransactions(draft) {
  return draft.operations.filter(
    (operation) => operation.type === 'transaction' && !operation.data.pending
  );
}
function computeReceivedTransactions(draft) {
  return draft.operations.filter(
    (operation) =>
      operation.type === 'transaction' &&
      new Decimal(operation.data.amount).gte(0) &&
      !operation.data.pending
  );
}
function computeSendTransactions(draft) {
  return draft.operations.filter(
    (operation) =>
      operation.type === 'transaction' &&
      new Decimal(operation.data.amount).lt(0) &&
      !operation.data.pending
  );
}

/**
 * HELPERS ----------------------
 */

var isMake = isCommand('make');
var isCancel = isCommand('cancel');
var isTake = isCommand('take');
var isPartialTakeResult = (execution) => isTake(execution) && execution.partialTakeResult;

function isCommand(name) {
  return (execution) => execution.command.toLowerCase() === name.toLowerCase();
}
function isMatchingMake({ execution, tx }) {
  return getAssetFullId(execution.underlyingAsset) === getAssetFullId(tx.asset);
}
function isMatchingPartialTakeResult({ execution, tx }) {
  return getAssetFullId(execution.pairAsset) === getAssetFullId(tx.asset);
}
function isMatchingCancel({ execution, tx }) {
  return getAssetFullId(execution.underlyingAsset) === getAssetFullId(tx.asset);
}
function isMatchingGiver({ execution, tx }) {
  return getAssetFullId(execution.pairAsset) === getAssetFullId(tx.asset);
}
function getExecutionOperation({ execution, pkHash, tx }) {
  return {
    type: 'execution',
    data: {
      operation: getOperationText({ execution, pkHash }),
      ...execution,
      makeProps: OrderConverter.getMakeClientProps(execution),
      takeProps: OrderConverter.getTakeClientProps(execution),
      pending: tx.confirmations === 0,
      partialTakeResult: execution.partialTakeResult === 1,
    },
  };
}
function getTransactionOperation({ tx }) {
  return {
    type: 'transaction',
    data: {
      ...tx,
      pending: tx.confirmations === 0,
    },
  };
}
function getOperationText({ execution, pkHash }) {
  return isMake(execution)
    ? OPERATIONS.OPEN_ORDER
    : isCancel(execution)
    ? OPERATIONS.CANCEL_ORDER
    : isPartialTakeResult(execution)
    ? OPERATIONS.PARTIAL_RESULT
    : pkHash === execution.takerPkHash
    ? OPERATIONS.TAKE_ORDER
    : OPERATIONS.ORDER_TAKEN;
}

function indexOrLength(items, callback) {
  const index = items.findIndex(callback);
  return index > -1 ? index : items.length;
}
function indexOfFirstTxConfirmationOrLength(txs = []) {
  return indexOrLength(txs, (t) => t.confirmations > 0);
}
